import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { RootState } from 'state/store';
import {
  createFilterSetting,
  fetchFilterSettingByType,
  selectFiltersSettingsTypeId,
  updateFilterSetting,
} from 'state/Settings/Filters/FiltersSlice';
import { setSelectedProjetOwnerIds } from 'state/ProjectOwners/projectOwnersSlice';
import { ProjectsListFilters } from 'types/store/projectsList';
import { SETTINGS_FILTERS } from 'utils/constants';
import {
  QuickFilter,
  DATE_RANGE_FILTER_KEYS,
  isDateRangeFilterType,
} from 'utils/constants/projectsList';
import { PersistentFilterTypeKeys } from 'utils/types/filters';
import { setSelectedCategoryIds } from './Categories/categoriesSlice';
import { setSelectedProcessIds } from './Processes/processesSlice';
import { setSelectedStageIds } from './Processes/stagesSlice';
import { setSelectedBusinessTeamIds } from './Teams/businessTeamsSlice';
import { setSelectedLdTeamIds } from './Teams/ldTeamsSlice';

interface FiltersState {
  search?: string;
  sort?: string;
  quickFilters?: QuickFilter[];
  appliedFilters?: ProjectsListFilters;
}

const initialState: FiltersState = {};

export const upsertFiltersSettings = createAsyncThunk(
  'projectsList/filters/upsertFiltersSettings',
  async (
    { filterTypeKey }: { filterTypeKey: PersistentFilterTypeKeys },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const filtersSettingsTypeId = selectFiltersSettingsTypeId(
      state,
      filterTypeKey
    );
    const { appliedFilters, quickFilters } = state.projectsList.filters.values;
    if (filtersSettingsTypeId) {
      await dispatch(
        updateFilterSetting({
          id: filtersSettingsTypeId,
          updateFields: {
            filter_settings: { ...appliedFilters, quickFilters },
          },
        })
      );
    } else {
      await dispatch(
        createFilterSetting({
          filter_type: filterTypeKey,
          filter_settings: { ...appliedFilters, quickFilters },
        })
      );
    }
    if (appliedFilters) {
      dispatch(setSelectedProjetOwnerIds(appliedFilters?.owners ?? []));
      dispatch(setSelectedLdTeamIds(appliedFilters?.teams ?? []));
      dispatch(setSelectedBusinessTeamIds(appliedFilters?.businessTeams ?? []));
      dispatch(setSelectedProcessIds(appliedFilters?.process ?? []));
      dispatch(setSelectedStageIds(appliedFilters?.stage ?? []));
      dispatch(setSelectedCategoryIds(appliedFilters?.category ?? []));
    }
  }
);

const filtersSlice = createSlice({
  name: 'projectsList/filters',
  initialState,
  reducers: {
    setSearchValue: (state, action) => {
      state.search = action.payload;
    },
    setSortValue: (state, action) => {
      state.sort = action.payload;
    },
    setFilter: (state, action) => {
      state.appliedFilters = action.payload;
    },
    updateFilter: (state, action) => {
      state.appliedFilters = {
        ...state.appliedFilters,
        ...action.payload,
      };
    },
    setQuickFilters: (state, action) => {
      state.quickFilters = action.payload;
    },
    resetFilters: (state) => {
      state.appliedFilters = undefined;
      state.quickFilters = undefined;
      state.search = undefined;
      state.sort = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFilterSettingByType.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }
      if (
        action.payload.filter_type === SETTINGS_FILTERS.ACTIVE_PROJECTS_TABLE ||
        action.payload.filter_type ===
          SETTINGS_FILTERS.CONSOLIDATED_PROJECTS_TABLE
      ) {
        const { filter_settings } = action.payload;
        const { quickFilters, ...rest } = filter_settings;
        state.appliedFilters = rest;
        state.quickFilters = quickFilters;
      }
    });
  },
});

export const {
  setSearchValue,
  setSortValue,
  setFilter,
  updateFilter,
  setQuickFilters,
  resetFilters,
} = filtersSlice.actions;

const getFiltersState = (state: RootState) => state.projectsList.filters.values;

export const selectSearchValue = createSelector(
  [getFiltersState],
  (filtersState: FiltersState) => filtersState.search
);

export const selectIsSearchValueEmpty = createSelector(
  [selectSearchValue],
  (searchValue) => searchValue === undefined || searchValue === ''
);

export const selectSortValue = createSelector(
  [getFiltersState],
  (filtersState: FiltersState) => filtersState.sort
);

export const selectAppliedFilters = createSelector(
  [getFiltersState],
  (filtersState: FiltersState) => {
    const { appliedFilters = {} } = filtersState;
    return Object.fromEntries(
      Object.entries(appliedFilters).filter(([key, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }

        if (DATE_RANGE_FILTER_KEYS.includes(key)) {
          return isDateRangeFilterType(value);
        }

        return value !== undefined && value !== '';
      })
    );
  }
);

export const selectAreFiltersEmpty = createSelector(
  [selectAppliedFilters],
  (appliedFilters) => {
    return Object.values(appliedFilters).every(
      (value) => value === undefined || value === ''
    );
  }
);

export const selectQuickFilters = createSelector(
  [getFiltersState],
  (filtersState: FiltersState) => filtersState.quickFilters
);

export const selectFilterParams = createSelector(
  [
    selectAppliedFilters,
    selectSortValue,
    selectSearchValue,
    selectQuickFilters,
  ],
  (appliedFilters, sort, search, quickFilters) => {
    return {
      filter: appliedFilters,
      search,
      sort,
      quickFilters,
    };
  }
);

export default filtersSlice.reducer;
